import { useTranslate } from '@refinedev/core';
import { Flex, Typography, theme } from 'antd';
import { CheckOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { ShowLink } from '@/components/common/ShowLink';
import { OutcomeIcon } from '@/components/common/OutcomeIcon';
import { OutcomeRow } from '@/components/common/OutcomeRow';
import { OutcomeSummary } from '@/components/common/OutcomeSummary';
import type { SyncReviewOutcome, SyncReviewStatus } from './syncReview';
import {
  countByStatus,
  isMutedStatus,
  problemCount,
  statusTone,
  successPercent,
} from './syncReview';

const { Text } = Typography;

interface SyncReviewResultProps {
  outcomes: SyncReviewOutcome[];
  /** Publication titles by id; falls back to `#id`. */
  titles?: Record<number, string>;
  /**
   * A catalog scan can walk a thousand ids: render no per-publication rows
   * at all, only the summary bar and one count per status.
   */
  countsOnly?: boolean;
}

/** Every status, in the order the count lines read best. */
const STATUSES: SyncReviewStatus[] = [
  'refreshed',
  'auto_refreshed',
  'skipped',
  'failed',
  'no_diff',
  'not_found',
];

/** Filled means a request was sent; outlined means the wizard only looked. */
const ICONS: Record<
  SyncReviewStatus,
  { glyph: React.ReactNode; variant: 'filled' | 'outlined' }
> = {
  refreshed: { glyph: <CheckOutlined />, variant: 'filled' },
  auto_refreshed: { glyph: <CheckOutlined />, variant: 'filled' },
  failed: { glyph: <CloseOutlined />, variant: 'filled' },
  skipped: { glyph: <MinusOutlined />, variant: 'outlined' },
  no_diff: { glyph: <MinusOutlined />, variant: 'outlined' },
  not_found: { glyph: <MinusOutlined />, variant: 'outlined' },
};

/** Summary bar plus one row per publication, in the order they were walked. */
export const SyncReviewResult = ({
  outcomes,
  titles,
  countsOnly = false,
}: SyncReviewResultProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publications.sync_review.result.${key}`, opts);

  const problems = problemCount(outcomes);
  const refreshed =
    countByStatus(outcomes, 'refreshed') +
    countByStatus(outcomes, 'auto_refreshed');

  return (
    <Flex vertical gap={token.marginSM}>
      <OutcomeSummary
        headline={
          problems > 0
            ? k('header_problems', { count: problems })
            : k('header_ok', { count: refreshed })
        }
        aside={k('publications', { count: outcomes.length })}
        percent={successPercent(outcomes)}
        exception={countByStatus(outcomes, 'failed') > 0}
        progressLabel={k('publications', { count: outcomes.length })}
      />
      {countsOnly ? (
        <Flex vertical gap={token.marginXXS}>
          {STATUSES.filter((s) => countByStatus(outcomes, s) > 0).map((s) => (
            <Text key={s} type="secondary">
              {k(`counts.${s}`, { count: countByStatus(outcomes, s) })}
            </Text>
          ))}
        </Flex>
      ) : (
        <Flex
          vertical
          gap={token.marginXS}
          style={{ maxHeight: 420, overflowY: 'auto' }}
        >
          {outcomes.map((o) => {
            const tone = statusTone(o.status);
            const badge = ICONS[o.status];
            return (
              <OutcomeRow
                key={o.publicationId}
                tone={tone}
                muted={isMutedStatus(o.status)}
                icon={
                  <OutcomeIcon tone={tone} variant={badge.variant}>
                    {badge.glyph}
                  </OutcomeIcon>
                }
                title={
                  <Flex gap={token.marginXS} align="baseline" wrap>
                    <Text strong>
                      {titles?.[o.publicationId] ?? `#${o.publicationId}`}
                    </Text>
                    <ShowLink
                      resource="user_catalog.publications"
                      id={o.publicationId}
                    >
                      #{o.publicationId}
                    </ShowLink>
                  </Flex>
                }
              >
                <Text
                  type={o.status === 'failed' ? 'danger' : 'secondary'}
                  style={{
                    display: 'block',
                    color:
                      o.status === 'refreshed' || o.status === 'auto_refreshed'
                        ? token.colorSuccess
                        : undefined,
                  }}
                >
                  {k(`status.${o.status}`)}
                </Text>
                {o.error && (
                  <Text type="danger" style={{ display: 'block' }}>
                    {o.error}
                  </Text>
                )}
              </OutcomeRow>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};
